import { CreditCard, Search } from "../../../components/icons";
import { Button } from "../../../components/ui/button";
import { Card, CardContent, CardHeader } from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import { FinancialRow } from "./FinancialRow";

interface FinancialTableProps {
  lang: "pt" | "en";
  t: any;
  activeTab: 'incomes' | 'expenses' | 'all' | 'insurance';
  setActiveTab: (tab: 'incomes' | 'expenses' | 'all' | 'insurance') => void;
  statusFilter: "all" | "paid" | "pending" | "insurance_pending";
  setStatusFilter: (filter: "all" | "paid" | "pending" | "insurance_pending") => void;
  search: string;
  setSearch: (search: string) => void;
  loading: boolean;
  filteredItems: any[];
  insuranceProviders: any[];
  resolveSpecialtyName: (p: any) => string;
  handleMarkAsPaid: (p: any) => void;
  handleDelete: (p: any) => void;
  handleStopRecurrence: (id: number) => void;
  onEditAmount: (item: any, newAmountCents: number) => void;
}

export function FinancialTable({
  lang,
  t,
  activeTab,
  setActiveTab,
  statusFilter,
  setStatusFilter,
  search,
  setSearch,
  loading,
  filteredItems,
  insuranceProviders,
  resolveSpecialtyName,
  handleMarkAsPaid,
  handleDelete,
  handleStopRecurrence,
  onEditAmount,
}: FinancialTableProps) {
  return (
    <Card className="shadow-sm">
      <CardHeader className="px-6 py-4 border-b">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1 bg-neutral-50 p-1 rounded-lg w-fit border border-neutral-100">
              <Button 
                variant={activeTab === 'all' ? "primary" : "ghost"} 
                size="sm" 
                className="h-7 text-[10px] px-4 font-bold uppercase tracking-wider"
                onClick={() => setActiveTab('all')}
              >
                {t.tabs.all}
              </Button>
              <Button 
                variant={activeTab === 'incomes' ? "primary" : "ghost"} 
                size="sm" 
                className="h-7 text-[10px] px-4 font-bold uppercase tracking-wider"
                onClick={() => setActiveTab('incomes')}
              >
                {t.tabs.incomes}
              </Button>
              <Button 
                variant={activeTab === 'expenses' ? "primary" : "ghost"} 
                size="sm" 
                className="h-7 text-[10px] px-4 font-bold uppercase tracking-wider"
                onClick={() => setActiveTab('expenses')}
              >
                {t.tabs.expenses}
              </Button>
            </div>

            <div className="h-4 w-px bg-neutral-200" />

            <div className="flex items-center gap-1 bg-neutral-50 p-1 rounded-lg w-fit border border-neutral-100">
              <Button 
                variant={statusFilter === "all" ? "primary" : "ghost"} 
                size="sm" 
                className="h-7 text-[10px] px-3 font-semibold uppercase"
                onClick={() => setStatusFilter("all")}
              >
                {t.filters.all}
              </Button>
              <Button 
                variant={statusFilter === "pending" ? "primary" : "ghost"} 
                size="sm" 
                className="h-7 text-[10px] px-3 font-semibold uppercase"
                onClick={() => setStatusFilter("pending")}
              >
                {t.filters.pending}
              </Button>
              <Button 
                variant={statusFilter === "paid" ? "primary" : "ghost"} 
                size="sm" 
                className="h-7 text-[10px] px-3 font-semibold uppercase"
                onClick={() => setStatusFilter("paid")}
              >
                {t.filters.paid}
              </Button>
            </div>
          </div>

          <div className="relative w-full md:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
            <Input 
              className="pl-9 h-10 bg-neutral-50 border-neutral-200 focus:bg-white transition-all" 
              placeholder={t.filters.searchPlaceholder}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[250px] pl-6 uppercase text-[10px] tracking-widest">{t.table.client}</TableHead>
              <TableHead className="uppercase text-[10px] tracking-widest">{t.table.type}</TableHead>
              <TableHead className="uppercase text-[10px] tracking-widest">{t.table.insurance}</TableHead>
              <TableHead className="uppercase text-[10px] tracking-widest">{t.table.amount}</TableHead>
              <TableHead className="uppercase text-[10px] tracking-widest">{t.table.date}</TableHead>
              <TableHead className="uppercase text-[10px] tracking-widest">{t.table.status}</TableHead>
              <TableHead className="text-right pr-6 uppercase text-[10px] tracking-widest">{t.table.actions}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="h-32 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    <span className="text-sm text-neutral-500 font-medium">{t.loading}</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : filteredItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="h-32 text-center text-neutral-500">
                  <div className="flex flex-col items-center gap-2">
                    <CreditCard className="h-8 w-8 text-neutral-200" />
                    <span className="text-sm font-medium">{t.empty}</span>
                  </div>
                </TableCell>
              </TableRow>
             ) : (
               filteredItems.map(p => (
                 <FinancialRow
                   key={p.id}
                   item={p}
                   lang={lang}
                   t={t}
                   insuranceProviders={insuranceProviders}
                   resolveSpecialtyName={resolveSpecialtyName}
                   handleMarkAsPaid={handleMarkAsPaid}
                   handleDelete={handleDelete}
                   handleStopRecurrence={handleStopRecurrence}
                   onEditAmount={onEditAmount}
                 />
               ))
             )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
